import getCart from '@/api/get/cart';
import addToCartKWC from '@/api/post/addToCartKWC';
import MenuItem from '@/kenwood/configurator/components/accessories/MenuItem';
import { useConfiguratorContext } from '@/kenwood/configurator/context';
import {
  handleRemoveConnection,
  handleRemoveProduct,
} from '@/kenwood/configurator/utils';
import { pushAccessoryAdd } from '@/utils/Controllers/GTM/Operations/Kenwood/Configurator';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useSetupStep2 from '@/kenwood/configurator/hooks/useSetupStep2';

const Menu = () => {
  const { t } = useTranslation();
  const [
    {
      isOwner,
      stepTwoMenuOpen,
      cartEntries,
      selectedCategory,
      selectedProduct,
      selectedConnection,
    },
    { setCartEntries, setSelectedConnection, setCartData },
  ] = useConfiguratorContext();

  const { productTitle } = useSetupStep2();

  const [menuLevel, setMenuLevel] = useState(1);
  // Categories of accessories
  const [categories, setCategories] = useState([]);

  // selected accessory category
  const [selectedMenuCategory, setSelectedMenuCategory] = useState(null);

  // Selected accessory
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const selectedAccessoryImage = useMemo(
    () =>
      selectedAccessory?.dlgConfiguratorImages.find(
        img => img.format === 'lifestyle',
      )?.l?.url,
    [selectedAccessory],
  );

  const [isConnectionAdded, setIsConnectionAdded] = useState(false);

  // Cart operations UI
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRemovingFromCart, setIsRemovingFromCart] = useState(false);

  // Ref for detecting clicks outside menu
  const menuRef = useRef(null);

  // Set the accessories categories
  useEffect(() => {
    if (selectedCategory) {
      setCategories(selectedCategory?.accessories);
    }
  }, [selectedCategory]);

  // Set the Connection added for UI purposes
  useEffect(() => {
    setIsConnectionAdded(isOwner && selectedConnection?.code);
  }, [selectedConnection]);

  // If we have the adaptor and the user is the owner we have to show the adaptor image
  const productImage = useMemo(
    () => selectedProduct?.image?.m?.url || selectedCategory?.image?.m?.url,
    [selectedProduct, selectedCategory],
  );
  // If we have the adaptor and the user is the owner we have to show the product name
  const productMenuTitle = useMemo(
    () =>
      isConnectionAdded
        ? selectedConnection?.name ||
          t('configurator:configurator.menu.connector.name')
        : productTitle,
    [selectedConnection, isConnectionAdded, productTitle],
  );
  const accessoriesCount = selectedCategory.accessories.reduce(
    (acc, category) => acc + category.accessoriesProducts.length,
    0,
  );

  // Category Images fallback
  const categoryImageFallback = `${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/category-item.png`;
  const categoryActiveImageFallback = `${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/category-item-active.png`;

  // Connection image
  const connectionImage = useMemo(
    () =>
      selectedConnection?.images?.find(img => img.format === 'thumbnail')
        ?.url ||
      `${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/adaptor.png`,
    [selectedConnection],
  );

  const calculateCategoryQuantity = categoryCode => {
    const categoryEntries = cartEntries.filter(
      entry => entry.productCategory === categoryCode,
    );
    return categoryEntries.reduce((acc, entry) => acc + entry.quantity, 0);
  };

  const calculateAccessoryQuantity = accessoryCode => {
    const accessoryEntry = cartEntries.find(
      entry => entry.productCode === accessoryCode,
    );
    return accessoryEntry?.quantity || 0;
  };

  const checkMenuItemToDisable = category =>
    category &&
    category.accessoriesProducts &&
    category.accessoriesProducts.filter(
      accessoryProduct =>
        accessoryProduct?.stock.stockLevelStatus !== 'outOfStock',
    ).length < 1;

  const handleAccessoryCategoryClick = category => {
    // In case the user clicks on the same category, close the menu
    if (category.code === selectedMenuCategory?.code) {
      setMenuLevel(1);
      setSelectedMenuCategory(null);
      setSelectedAccessory(null);
    } else {
      setSelectedMenuCategory(category);
      setMenuLevel(2);
    }
  };

  const handleAccessoryClick = accessoryProduct => {
    // In case the user clicks on the same accessory, close the menu
    if (accessoryProduct.code === selectedAccessory?.code) {
      setMenuLevel(2);
      setSelectedAccessory(null);
    } else {
      setSelectedAccessory(accessoryProduct);
      setMenuLevel(3);
    }
  };

  const selectedAccessoryQuantity = useMemo(
    () => calculateAccessoryQuantity(selectedAccessory?.code),
    [selectedAccessory, cartEntries],
  );

  const handleAddProduct = e => {
    e.stopPropagation();
    pushAccessoryAdd(
      isOwner,
      selectedMenuCategory?.name,
      selectedAccessory.name,
    );
    setIsAddingToCart(true);

    addToCartKWC({
      skuCode: selectedAccessory?.code,
      qty: 1,
      successCB: () => {
        getCart({
          successCB: data => {
            setCartEntries(data?.entries || []);
            setCartData(data);
            setIsAddingToCart(false);
          },
        });
      },
      failCB: () => setIsAddingToCart(false),
    });
  };

  // Close menu levels when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuLevel(1);
        setSelectedMenuCategory(null);
        setSelectedAccessory(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={menuRef}
      className={`ken-configurator__accessories-menu ${
        stepTwoMenuOpen ? '-mobile-open' : ''
      }`}
    >
      {/* First Level */}
      <div
        className={`ken-configurator__accessories-menu-lvl1 ${
          menuLevel === 1 ? '-active' : ''
        }`}
      >
        <div
          className={`ken-configurator__accessories-menu-item -no-pointer ken-configurator__accessories-menu-product ${
            isOwner && !isConnectionAdded ? '-hidden' : ''
          }`}
        >
          <div className='ken-configurator__accessories-menu-product-img-wrapper'>
            <img
              src={isConnectionAdded ? connectionImage : productImage}
              alt={productMenuTitle}
              className='ken-configurator__accessories-menu-product-img'
            />
          </div>
          <div className='ken-configurator__accessories-menu-product-content-wrapper'>
            {isOwner && isConnectionAdded && (
              <>
                <span className='ken-configurator__accessories-menu-product-label'>
                  {t('configurator:configurator.menu.have.added')}
                  <br />
                  <strong>{productMenuTitle}</strong>
                </span>
                <button
                  type='button'
                  className='ken-configurator__accessories-menu-link-btn ken-configurator__accessories-menu-product-remove'
                  onClick={() =>
                    handleRemoveConnection({
                      isOwner,
                      selectedCategory,
                      selectedConnection,
                      setSelectedConnection,
                      setCartEntries,
                    })
                  }
                >
                  {t('configurator:configurator.menu.remove')}
                </button>
              </>
            )}
            {!isOwner && (
              <span className='ken-configurator__accessories-menu-product-label'>
                {t('configurator:configurator.menu.are.personalizing')}
                <br />
                <strong>{productMenuTitle}</strong>
              </span>
            )}
          </div>
        </div>
        <div
          className={`ken-configurator__accessories-menu-wrapper ${
            isOwner && !isConnectionAdded ? '-no-product' : ''
          }`}
        >
          <div className='ken-configurator__accessories-menu-item -no-pointer ken-configurator__accessories-menu-count'>
            <strong>
              {t('configurator:configurator.menu.choose.between')}{' '}
              {accessoriesCount}{' '}
              {t('configurator:configurator.menu.different.accessories')}
            </strong>
          </div>
          {categories &&
            categories.map(category => (
              <MenuItem
                key={category.code}
                image={category.image?.s?.url || categoryImageFallback}
                activeImage={
                  category.selectedImage?.s?.url || categoryActiveImageFallback
                }
                name={category.name || ''}
                price=''
                onClick={() => handleAccessoryCategoryClick(category)}
                isActive={category.code === selectedMenuCategory?.code}
                isDisabled={checkMenuItemToDisable(category)}
                macroQuantity={calculateCategoryQuantity(category.code)}
              />
            ))}
        </div>
      </div>

      {/* Second Level */}
      {(menuLevel === 2 || menuLevel === 3) && (
        <div
          className={`ken-configurator__accessories-menu-lvl2 ${
            menuLevel === 2 ? '-active' : ''
          }`}
        >
          <button
            className='ken-configurator__accessories-menu-item ken-configurator__accessories-menu-back'
            type='button'
            onClick={() => {
              setMenuLevel(1);
              setSelectedMenuCategory(null);
            }}
          >
            <img
              src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/arrow-back.svg`}
              alt={t('configurator:configurator.alt.back')}
              className='ken-configurator__accessories-menu-back-img'
            />
            <span className='ken-configurator__accessories-menu-back-category-img-wrapper'>
              <img
                src={selectedMenuCategory.image?.s?.url}
                alt={selectedMenuCategory.name}
                className='ken-configurator__accessories-menu-back-category-img-img'
              />
              <span className='ken-configurator__accessories-menu-back-category-img-filter' />
            </span>
            <span className='ken-configurator__accessories-menu-back-category-label'>
              <strong>{selectedMenuCategory.name}</strong>
            </span>
          </button>
          <div className='ken-configurator__accessories-menu-wrapper'>
            {selectedMenuCategory.accessoriesProducts
              .filter(
                accessoryProduct =>
                  accessoryProduct?.stock.stockLevelStatus !== 'outOfStock',
              )
              .map(accessoryProduct => (
                <MenuItem
                  key={accessoryProduct.code}
                  image={
                    accessoryProduct?.dlgConfiguratorImages.find(
                      image => image.format === 'thumbnail',
                    )?.s?.url
                  }
                  name={accessoryProduct.name || ''}
                  price={accessoryProduct.price?.formattedValue || ''}
                  onClick={() => handleAccessoryClick(accessoryProduct)}
                  isActive={accessoryProduct.code === selectedAccessory?.code}
                  isSecondLevel
                  microQuantity={calculateAccessoryQuantity(
                    accessoryProduct.code,
                  )}
                  onRemove={() => {
                    handleRemoveProduct({
                      productCode: accessoryProduct.code,
                      cartEntries,
                      setCartEntries,
                      setCartData,
                      setIsRemovingFromCart,
                      isOwner,
                      categoryName: selectedMenuCategory?.name,
                      productName: selectedAccessory?.name,
                    });
                  }}
                  removeDisabled={isRemovingFromCart}
                />
              ))}
          </div>
        </div>
      )}

      {/* Third Level */}
      {menuLevel === 3 && (
        <div
          className={`ken-configurator__accessories-menu-lvl3 ${
            menuLevel === 3 ? '-active' : ''
          }`}
        >
          <button
            className='ken-configurator__accessories-menu-item ken-configurator__accessories-menu-back'
            type='button'
            onClick={() => {
              setMenuLevel(2);
              setSelectedAccessory(null);
            }}
          >
            <img
              src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/arrow-back.svg`}
              alt={t('configurator:configurator.alt.back')}
              className='ken-configurator__accessories-menu-back-img'
            />
            <span className='ken-configurator__accessories-menu-back-category-label'>
              <strong>{t('configurator:configurator.menu.detail')}</strong>
            </span>
          </button>
          <div className='ken-configurator__accessories-menu-accessory'>
            <div className='ken-configurator__accessories-menu-accessory-info'>
              <div className='ken-configurator__accessories-menu-accessory-info-wrapper'>
                <strong className='ken-configurator__accessories-menu-accessory-info-name'>
                  {selectedAccessory.name}
                </strong>
                <div className='ken-configurator__accessories-menu-accessory-info-price'>
                  <span>{selectedAccessory.price.formattedValue}</span>
                </div>
              </div>
              <button
                type='button'
                className='ken-button--primary ken-configurator__accessories-menu-accessory-info-add'
                onClick={handleAddProduct}
                disabled={isAddingToCart}
              >
                <span>{t('configurator:configurator.menu.add')}</span>
                <img
                  src={`${DLG.config.distResourcePath}/assets/images/kenwood/configurator/menu/plus.svg`}
                  alt={t('configurator:configurator.menu.add')}
                />
              </button>
            </div>
            {selectedAccessoryQuantity > 0 && (
              <div className='ken-configurator__accessories-menu-accessory-info-quantity'>
                <div className='ken-configurator__accessories-menu-accessory-info-quantity-number'>
                  {selectedAccessoryQuantity}
                </div>
                <div className='ken-configurator__accessories-menu-accessory-info-quantity-label'>
                  {t('configurator:configurator.menu.added')}
                </div>
                <button
                  type='button'
                  className='ken-configurator__accessories-menu-link-btn ken-configurator__accessories-menu-accessory-info-quantity-remove'
                  onClick={() =>
                    handleRemoveProduct({
                      productCode: selectedAccessory.code,
                      cartEntries,
                      setCartEntries,
                      setCartData,
                      setIsRemovingFromCart,
                      isOwner,
                      categoryName: selectedMenuCategory?.name,
                      productName: selectedAccessory?.name,
                    })
                  }
                  disabled={isRemovingFromCart}
                >
                  {t('configurator:configurator.menu.remove')}
                </button>
              </div>
            )}
            <div
              className='ken-configurator__accessories-menu-accessory-info-description'
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: selectedAccessory.shortDescription,
              }}
            />
            <img
              src={selectedAccessoryImage}
              alt={selectedAccessory.name}
              className='ken-configurator__accessories-menu-accessory-info-img'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu;
